// ============================================================================
// 자동화 템플릿 선택 모달 (CHG-047: REQ-V47-013)
// ============================================================================

import { useState } from 'react';
import { X, Loader2, CheckCircle, XCircle } from 'lucide-react';
import toast from 'react-hot-toast';
import { useAutomationTemplates, useApplyTemplate } from '../../hooks/useAutomation';
import type { WorkflowStatusInfo } from '../../services/projectService';

interface Props {
  projectId: string;
  statuses: WorkflowStatusInfo[];
  onClose: () => void;
}

// Status names used by each template (for preview matching)
const TEMPLATE_STATUS_NAMES: Record<string, string[]> = {
  GITHUB_FLOW_BASIC: ['In Progress', 'In Review', 'Approved', 'Done'],
  GIT_FLOW_BASIC: ['In Progress', 'In Review', 'Done'],
  QA_PROCESS: ['In Progress', 'In Review', 'QA Testing', 'Ready to Deploy'],
  RELEASE_AUTOMATION: ['Done'],
};

export default function TemplateSelectModal({ projectId, statuses, onClose }: Props) {
  const { data: templates } = useAutomationTemplates(projectId);
  const applyMutation = useApplyTemplate(projectId);
  const [selectedType, setSelectedType] = useState<string | null>(null);

  const matchStatus = (name: string): boolean => {
    const lower = name.toLowerCase();
    return statuses.some((s) =>
      s.name.toLowerCase() === lower ||
      s.name.toLowerCase().includes(lower) ||
      lower.includes(s.name.toLowerCase()),
    );
  };

  const handleApply = async () => {
    if (!selectedType) return;
    try {
      const result = await applyMutation.mutateAsync(selectedType);
      toast.success(`${result.data.totalRules}개 규칙 생성 (${result.data.matched}개 활성, ${result.data.unmatched}개 비활성)`);
      if (result.warnings.length > 0) {
        result.warnings.forEach((w) => toast(w, { icon: '⚠️' }));
      }
      onClose();
    } catch {
      toast.error('템플릿 적용에 실패했습니다.');
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-lg mx-4 max-h-[80vh] overflow-y-auto">
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200">
          <h2 className="text-base font-semibold text-gray-900">자동화 템플릿 선택</h2>
          <button onClick={onClose} className="p-1 text-gray-400 hover:text-gray-600"><X className="size-5" /></button>
        </div>

        <div className="px-5 py-4 space-y-3">
          {templates?.map((t) => {
            const isSelected = selectedType === t.templateType;
            const statusNames = TEMPLATE_STATUS_NAMES[t.templateType] ?? [];
            return (
              <button
                key={t.templateType}
                onClick={() => setSelectedType(t.templateType)}
                className={`w-full text-left p-4 border rounded-xl transition-all ${isSelected ? 'border-primary-500 bg-primary-50 ring-2 ring-primary-200' : 'border-gray-200 hover:border-gray-300'}`}
              >
                <div className="flex items-center justify-between">
                  <div className="text-sm font-semibold text-gray-900">{t.name}</div>
                  <span className="text-sm text-gray-400">{t.ruleCount}개 규칙</span>
                </div>
                <div className="text-sm text-gray-500 mt-1">{t.description}</div>
                {isSelected && statusNames.length > 0 && (
                  <div className="mt-3 space-y-1 border-t border-gray-200 pt-2">
                    <div className="text-[11px] font-semibold text-gray-500 mb-1">상태 매칭 미리보기:</div>
                    {statusNames.map((sn) => {
                      const matched = matchStatus(sn);
                      return (
                        <div key={sn} className="flex items-center gap-1.5 text-sm">
                          {matched ? <CheckCircle className="size-3.5 text-green-600" /> : <XCircle className="size-3.5 text-red-500" />}
                          <span className={matched ? 'text-gray-700' : 'text-red-600'}>{sn}</span>
                          {matched ? <span className="text-gray-400">→ 매칭됨</span> : <span className="text-red-400">→ 비활성으로 생성</span>}
                        </div>
                      );
                    })}
                  </div>
                )}
              </button>
            );
          })}
        </div>

        <div className="flex items-center justify-end gap-2 px-5 py-4 border-t border-gray-200">
          <button onClick={onClose} className="px-4 py-2 text-sm font-medium text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50">취소</button>
          <button onClick={handleApply} disabled={!selectedType || applyMutation.isPending} className="inline-flex items-center gap-1.5 px-4 py-2 text-sm font-medium text-white bg-primary-600 rounded-lg hover:bg-primary-700 disabled:opacity-50">
            {applyMutation.isPending && <Loader2 className="size-4 animate-spin" />}
            적용
          </button>
        </div>
      </div>
    </div>
  );
}
